'use client';

import Image from '@/components/Image';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import { EPlatform } from '@/utils/enums/platform-enum';
import { useTranslations } from '@/i18n/use-translations';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

// 导航项数据类型
export interface NavigationItem {
  title: string;
  image: string;
  wap_image?: string; // 移动端图片
  alt?: string;
}

export interface ProductNavigationProps {
  // 导航数据
  navigationData: NavigationItem[];

  // 选中项索引（受控）
  selectedIndex?: number;

  // 默认选中项索引
  defaultSelectedIndex?: number;

  // 事件回调
  onNavigationClick?: (item: NavigationItem, index: number) => void;

  // 是否启用吸顶效果
  isSticky?: boolean;

  // 是否禁用自动选中功能（在滚动过程中使用）
  disableAutoSelect?: boolean;

  // 样式配置
  className?: string;
}

export default function ProductNavigation({
  navigationData = [],
  selectedIndex: controlledSelectedIndex,
  defaultSelectedIndex = 0,
  onNavigationClick,
  isSticky = true,
  disableAutoSelect = false,
  className = '',
}: ProductNavigationProps) {
  // 获取设备类型
  const { deviceType } = useContext(ResponsiveContext);
  const t = useTranslations('activity.springSale');

  // 根据设备类型计算移动端的 top 值
  const getMobileTopClass = () => {
    const isApp = [EPlatform.ANDROID, EPlatform.IOS].includes(deviceType);
    return isApp ? 'top-0' : 'top-[11.733vw]';
  };

  // 选中状态管理（支持受控和非受控模式）
  const [internalSelectedIndex, setInternalSelectedIndex] = useState(defaultSelectedIndex);
  const selectedIndex =
    controlledSelectedIndex !== undefined ? controlledSelectedIndex : internalSelectedIndex;

  // 滚动容器引用
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Array<HTMLDivElement | null>>([]);

  // 滚动到选中项居中
  const scrollToCenter = useCallback((index: number) => {
    const container = scrollContainerRef.current;
    const item = itemRefs.current[index];

    if (container && item) {
      const containerWidth = container.offsetWidth;
      const itemLeft = item.offsetLeft;
      const itemWidth = item.offsetWidth;

      // 计算居中位置
      const scrollLeft = itemLeft - (containerWidth - itemWidth) / 2;

      container.scrollTo({
        left: scrollLeft,
        behavior: 'smooth',
      });
    }
  }, []);

  // 处理导航项点击
  const handleNavigationClick = useCallback(
    (item: NavigationItem, index: number) => {
      // 如果是非受控模式，更新内部状态
      if (controlledSelectedIndex === undefined) {
        setInternalSelectedIndex(index);
      }

      // 滚动到居中位置
      scrollToCenter(index);

      onNavigationClick?.(item, index);
    },
    [controlledSelectedIndex, onNavigationClick, scrollToCenter],
  );

  // 当选中索引变化时，滚动到居中位置
  useEffect(() => {
    if (selectedIndex !== undefined) {
      scrollToCenter(selectedIndex);
    }
  }, [selectedIndex, scrollToCenter]);

  const navigationItems = useMemo(
    () =>
      navigationData.map((item) => ({
        ...item,
        imageAlt:
          item.alt || (item.title === 'sunglasses' ? item.title : `${item.title} ${t('glasses')}`),
      })),
    [navigationData, t],
  );

  // 如果没有导航数据，不渲染组件
  if (!navigationData || navigationData.length === 0) {
    return null;
  }

  return (
    <div
      className={`product-navigation w-full bg-[#ffffff] ${
        isSticky ? `sticky ${getMobileTopClass()} lg:top-[100px] z-[15]` : 'static'
      } ${className}`}
    >
      <div className="navigation-container py-[2px] h-[100px] md:px-[100px] lg:px-[6.94vw] xl:px-[8.68vw] 2xl:px-[9.38vw] 3xl:px-[10.42vw] lg:py-[1.67vw] lg:h-[6vw] flex items-center">
        {/* 导航项容器 - 水平滚动 */}
        <div
          ref={scrollContainerRef}
          className="navigation-items flex gap-[7px] lg:gap-[0.83vw] overflow-x-auto scrollbar-hidden lg:justify-center lg:overflow-visible h-full lg:h-auto items-center lg:mx-auto"
        >
          {navigationItems.map((item, index) => {
            const isSelected = selectedIndex === index;

            return (
              <div
                key={`navigation-${index}-${item.title}`}
                ref={(el) => {
                  itemRefs.current[index] = el;
                }}
                className={`navigation-item flex-shrink-0 cursor-pointer transition-all duration-300 hover:opacity-90
                  lg:flex lg:flex-row lg:items-center lg:rounded-[1.46vw]
                  flex flex-col justify-end rounded-[8px] overflow-hidden relative
                  ${
                    isSelected
                      ? 'lg:bg-[#282828] lg:w-[10.42vw] lg:h-[2.92vw] w-[83px] h-[68px] lg:!bg-none border border-[#282828]'
                      : 'lg:border lg:border-[#808080] lg:w-[10.42vw] lg:h-[2.92vw] w-[70px] h-[58px] lg:!bg-none border-0 border-[#811919]'
                  }
                  ${index === 0 ? 'ml-[8px] lg:ml-0' : ''}
                  ${index === navigationData.length - 1 ? 'mr-[8px] lg:mr-0' : ''}`}
                onClick={() => handleNavigationClick(item, index)}
              >
                {/* 移动端背景图片 */}
                <div className="lg:hidden inset-0 w-full h-full flex-1 relative">
                  <Image
                    src={item.wap_image || item.image}
                    alt={item.imageAlt}
                    fill
                    className="object-cover h-full"
                    sizes="(max-width: 1024px) 83px, 0px"
                  />

                  {/* 移动端布局 - 底部文字条 */}
                  <div
                    className={`lg:hidden w-[104%] absolute bottom-0 left-0 ml-[-2%] ${isSelected ? 'bg-[#282828] h-[20px]' : 'bg-[#282828]/40 h-[17px]'}`}
                  >
                    {/* 文字区域 - 底部红色条 */}
                    <div
                      className={`relative z-10 h-full rounded-b-[6.5px] flex items-center justify-center w-full px-[4px] box-border ${
                        isSelected ? 'h-[18px]' : 'h-[20px]'
                      }`}
                    >
                      <span
                        className={`text-white text-[10px] text-center whitespace-nowrap leading-[12px] capitalize overflow-hidden text-ellipsis max-w-full ${
                          isSelected ? 'font-semibold' : 'font-medium'
                        }`}
                      >
                        {item.title}
                      </span>
                    </div>
                  </div>
                </div>
                {/* PC端布局 */}
                <div className="hidden lg:flex flex-row items-center w-[10.42vw] h-[2.92vw] px-[0.2vw]">
                  {/* 图片 */}
                  <div className="w-[2.5vw] h-[2.5vw] rounded-[1.46vw] overflow-hidden flex-shrink-0">
                    <Image
                      src={item.image || item.wap_image || ''}
                      alt={item.imageAlt}
                      width={48}
                      height={48}
                      className="object-cover w-full h-full"
                    />
                  </div>
                  {/* 标题 */}
                  <div className="flex-1 flex items-center justify-start ml-[0.83vw] w-[7vw]">
                    <span
                      className={`${isSelected ? 'text-[#ffffff]' : 'text-[#282828]'} text-[1.04vw] font-bold leading-[1.25vw] capitalize break-words`}
                    >
                      {item.title}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
